/* import __COLOCATED_TEMPLATE__ from './fin-readiness-qa-section.hbs'; */
/* RESPONSIBLE TEAM: team-ai-insights */
import Component from '@glimmer/component';
import { inject as service } from '@ember/service';
import type Store from '@ember-data/store';
import type ContentSuggestionsService from 'embercom/services/content-suggestions-service';
import { taskFor } from 'ember-concurrency-ts';
import { loadMessengerSettings } from 'embercom/lib/home/load-messenger-settings';
import { task } from 'ember-concurrency-decorators';
import { tracked } from '@glimmer/tracking';
import EmberObject, { action } from '@ember/object';
import type ThemeService from 'embercom/services/theme-service';
import type IntlService from 'embercom/services/intl';

interface Args {}

export default class FinReadinessQaSection extends Component<Args> {
  @service declare appService: $TSFixMe;
  @service declare store: Store;
  @service declare contentSuggestionsService: ContentSuggestionsService;
  @service declare themeService: ThemeService;
  @service declare intl: IntlService;
  @tracked messengerSettings: any;
  @tracked operatorIdentity: any;
  @tracked selectedQuestionAnswerPair: any;
  @tracked loadedQuestionAnswerPairs: any[] = [];
  constructor(owner: any, args: any) {
    super(owner, args);
    taskFor(this.loadMessengerSettings).perform();
    taskFor(this.loadOperatorIdentity).perform();
    taskFor(this.questionAnswerPairsLoader).perform();
  }

  @task({ drop: true })
  *loadMessengerSettings(): Generator<any, void, any> {
    this.messengerSettings = yield loadMessengerSettings(this);
    this.messengerSettings.previewConfiguration.isFinPreview = true;
  }

  @task({ drop: true })
  *loadOperatorIdentity(): Generator<any, void, any> {
    this.operatorIdentity = yield this.store.findRecord('operator-identity', 1);
  }

  @task({ drop: true })
  *questionAnswerPairsLoader(): Generator<any, any, any> {
    this.loadedQuestionAnswerPairs = yield taskFor(
      this.contentSuggestionsService.loadQuestionAnswerPairsForGettingStarted,
    ).perform();

    this.selectedQuestionAnswerPair = this.loadedQuestionAnswerPairs?.[0];
  }

  get finIdentity() {
    let identity = this.operatorIdentity.getProperties(
      'name',
      'isOperator',
      'avatarData',
      'avatarType',
    );
    identity = EmberObject.create(identity);
    identity.set('avatarData', {
      url: this.operatorIdentity.avatar.image_urls.square_128,
      avatarShape: this.operatorIdentity.avatarData.avatarShape,
    });
    return identity;
  }

  get isLoading() {
    return (
      taskFor(this.loadMessengerSettings).isRunning ||
      taskFor(this.loadOperatorIdentity).isRunning ||
      taskFor(this.questionAnswerPairsLoader).isRunning
    );
  }

  get conversationParts() {
    return [
      {
        app_id: this.appService.app.id,
        sub_type: 'comment',
        blocks: [
          {
            type: 'paragraph',
            text: this.intl.t('automation.ai-insights.fin-readiness.initial-message'),
          },
        ],
      },
      {
        app_id: this.appService.app.id,
        participant_id: 'test-participant-id',
        sub_type: 'comment',
        blocks: [
          {
            type: 'paragraph',
            text: this.selectedQuestionAnswerPair?.question,
          },
        ],
      },
      {
        app_id: this.appService.app.id,
        isBotPart: true,
        sub_type: 'comment',
        blocks: [
          {
            type: 'paragraph',
            text: this.selectedQuestionAnswerPair?.answer,
          },
        ],
      },
    ];
  }

  @action
  selectQuestionAnswerPair(questionAnswerPair: any) {
    this.selectedQuestionAnswerPair = questionAnswerPair;
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Reporting::Automation::AiInsights::FinReadiness::FinReadinessQaSection': typeof FinReadinessQaSection;
  }
}
